class YourAisle extends HTMLElement {
  async connectedCallback() {
    this.grid = this.querySelector('[data-aisle-grid]');
    this.status = this.querySelector('[data-aisle-status]');
    this.toggle = this.querySelector('[data-aisle-toggle]');
    this.initialLimit = Number(this.dataset.initialLimit || 12);
    const sources = await this.loadAllSources();
    this.items = this.collectItems(sources);
    this.expanded = false;

    if (!this.items.length) {
      this.remove();
      return;
    }

    this.render();
    this.dataset.aisleReady = 'true';
    this.addEventListener('click', (event) => this.onClick(event));
    this.trackImpressions();
  }

  async loadAllSources() {
    const sources = Array.from(this.querySelectorAll('[data-aisle-item]'));
    const visited = new Set();
    let nextPage = this.dataset.nextPage;

    while (nextPage && !visited.has(nextPage)) {
      visited.add(nextPage);
      try {
        const response = await fetch(nextPage, { headers: { Accept: 'text/html' } });
        if (!response.ok) break;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextAisle = documentFragment.querySelector('your-aisle');
        if (!nextAisle) break;
        sources.push(...nextAisle.querySelectorAll('[data-aisle-item]'));
        nextPage = nextAisle.dataset.nextPage;
      } catch (error) {
        break;
      }
    }

    return sources;
  }

  collectItems(sources) {
    const variants = new Map();

    sources.forEach((source, sourceIndex) => {
      const item = source.dataset;
      const existing = variants.get(item.variantId);
      const orderIndex = sourceIndex;

      if (existing) {
        if (!existing.orderKeys.has(item.orderKey)) {
          existing.orderKeys.add(item.orderKey);
          existing.orderCount += 1;
        }
        existing.quantity += Number(item.quantity || 1);
        if (orderIndex < existing.recency) {
          existing.recency = orderIndex;
          existing.lastPurchased = item.lastPurchased;
        }
        return;
      }

      variants.set(item.variantId, {
        ...item,
        orderCount: 1,
        orderKeys: new Set([item.orderKey]),
        quantity: Number(item.quantity || 1),
        recency: orderIndex
      });
    });

    return Array.from(variants.values()).sort((a, b) => {
      if (b.orderCount !== a.orderCount) return b.orderCount - a.orderCount;
      return a.recency - b.recency;
    });
  }

  render() {
    this.grid.replaceChildren();
    const visibleItems = this.expanded ? this.items : this.items.slice(0, this.initialLimit);
    visibleItems.forEach((item, index) => this.grid.append(this.createCard(item, index)));

    if (this.items.length > this.initialLimit) {
      this.toggle.hidden = false;
      this.toggle.textContent = this.expanded ? this.dataset.labelShowLess : this.dataset.labelShowAll;
    } else {
      this.toggle.hidden = true;
      this.toggle.textContent = '';
    }
  }

  createCard(item, index) {
    const article = document.createElement('article');
    article.className = 'your-aisle-card';
    article.dataset.variantId = item.variantId;
    article.dataset.rank = String(index + 1);

    const imageLink = document.createElement('a');
    imageLink.className = 'your-aisle-card__image';
    imageLink.href = item.url;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 160;
      image.height = 160;
      imageLink.append(image);
    }

    const content = document.createElement('div');
    content.className = 'your-aisle-card__content';

    const title = document.createElement('a');
    title.className = 'your-aisle-card__title full-unstyled-link';
    title.href = item.url;
    title.textContent = item.productTitle;

    const variant = document.createElement('p');
    variant.className = 'your-aisle-card__variant caption';
    if (item.variantTitle && item.variantTitle !== 'Default Title') variant.textContent = item.variantTitle;

    const meta = document.createElement('p');
    meta.className = 'your-aisle-card__meta caption';
    const frequency = this.dataset.labelBoughtTimes.replace('[count]', item.orderCount);
    const lastBought = this.dataset.labelLastBought.replace('[date]', item.lastPurchased);
    meta.textContent = item.orderCount > 1 ? `${frequency} · ${lastBought}` : lastBought;

    const footer = document.createElement('div');
    footer.className = 'your-aisle-card__footer';

    const price = document.createElement('span');
    price.className = 'your-aisle-card__price';
    price.textContent = item.price;

    const button = document.createElement('button');
    button.className = 'button button--secondary your-aisle-card__button';
    button.type = 'button';
    button.dataset.aisleAdd = '';
    button.textContent = this.dataset.labelAdd;

    footer.append(price, button);
    content.append(title, variant, meta, footer);
    article.append(imageLink, content);
    return article;
  }

  async onClick(event) {
    const toggle = event.target.closest('[data-aisle-toggle]');
    if (toggle) {
      this.expanded = !this.expanded;
      this.render();
      return;
    }

    const button = event.target.closest('[data-aisle-add]');
    if (!button || button.disabled) return;
    const card = button.closest('.your-aisle-card');
    const variantId = card?.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.textContent = this.dataset.labelAdding;
    this.status.textContent = '';

    try {
      const addPath = window.routes?.cart_add_url || '/cart/add';
      const response = await fetch(addPath.endsWith('.js') ? addPath : `${addPath}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.labelError);

      this.trackEvent('your_aisle_add', card);
      button.textContent = this.dataset.labelAdded;
      this.status.textContent = this.dataset.labelAddedMessage;
      if (this.dataset.context === 'cart') window.setTimeout(() => window.location.reload(), 450);
    } catch (error) {
      button.disabled = false;
      button.textContent = this.dataset.labelAdd;
      this.status.textContent = error.message || this.dataset.labelError;
    }
  }

  trackImpressions() {
    this.querySelectorAll('.your-aisle-card').forEach((card) => this.trackEvent('your_aisle_impression', card));
  }

  trackEvent(name, card) {
    const detail = {
      event: name,
      context: this.dataset.context,
      variantId: card?.dataset.variantId || '',
      rank: Number(card?.dataset.rank || 0)
    };
    document.dispatchEvent(new CustomEvent(`feltbox:${name}`, { detail }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `feltbox_${name}`, ...detail });
  }
}

if (!customElements.get('your-aisle')) customElements.define('your-aisle', YourAisle);
